import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { PageHeader } from '../../components/page';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { Tabs } from '../portfolio';
import { ConnectorsSection } from './connectors';
import { DevelopersSection } from './developers';
import { AgentsSection, InsurersSection, ProductLinesSection } from './referentials';
import { SecuritySection } from './security';
import { SubscriptionSection } from './subscription';
import { TenantSection } from './tenant';
import { UsersSection } from './users';

// Paramètres (BA-026, spec 08 §8). Les onglets réservés aux admins sont masqués aux autres rôles.

const TABS = [
  'cabinet',
  'assureurs',
  'produits',
  'agents',
  'utilisateurs',
  'connecteur',
  'developpeurs',
  'abonnement',
  'securite',
] as const;
type Tab = (typeof TABS)[number];
export const settingsSearchSchema = z.object({ tab: z.enum(TABS).optional() });

export function SettingsPage() {
  const t = useT();
  const auth = useAuth();
  const navigate = useNavigate();
  const admin = auth.can('administer');
  const { tab: requested } = useSearch({ strict: false }) as { tab?: Tab };
  const visible: { key: Tab; label: string }[] = [
    { key: 'cabinet', label: t.settings.tenant },
    { key: 'assureurs', label: t.settings.insurers },
    { key: 'produits', label: t.settings.productLines },
    { key: 'agents', label: t.settings.agents },
    ...(admin ? [{ key: 'utilisateurs' as const, label: t.settings.users }] : []),
    ...(admin ? [{ key: 'connecteur' as const, label: t.connectors.tab }] : []),
    ...(admin ? [{ key: 'developpeurs' as const, label: t.developers.title }] : []),
    ...(admin ? [{ key: 'abonnement' as const, label: t.subscription.tab }] : []),
    { key: 'securite', label: t.settings.security },
  ];
  const tab = visible.some((v) => v.key === requested) ? (requested as Tab) : 'cabinet';

  return (
    <>
      <PageHeader title={t.settings.title} />
      <Tabs
        label={t.settings.title}
        active={tab}
        tabs={visible}
        onChange={(key) => void navigate({ to: '/parametres', search: { tab: key } })}
      />
      {tab === 'cabinet' ? <TenantSection /> : null}
      {tab === 'assureurs' ? <InsurersSection /> : null}
      {tab === 'produits' ? <ProductLinesSection /> : null}
      {tab === 'agents' ? <AgentsSection /> : null}
      {tab === 'utilisateurs' ? <UsersSection /> : null}
      {tab === 'connecteur' ? <ConnectorsSection /> : null}
      {tab === 'developpeurs' ? <DevelopersSection /> : null}
      {tab === 'abonnement' ? <SubscriptionSection /> : null}
      {tab === 'securite' ? <SecuritySection /> : null}
    </>
  );
}
